import { SNAP_ORDER, type SnapType } from '@lakaran/engine';
import { CONSTRAINT_KINDS, CONSTRAINT_LABELS, CONSTRAINT_SHORT } from './editor/editor';
import type { CadEditor } from './editor/editor';
import type { CadRenderer } from './render/renderer';
import { ICONS, type IconName } from './icons';
import type { GraphStore } from '../nodes/index';
import { installFileActions, type FileActions } from './files';
import { createMenuButton, type MenuEntry } from './menu';

interface ToolButtonDef {
  name: string;
  label: string;
  title: string;
  icon: IconName;
}

/** Drawing primitives. */
const PRIMITIVES: ToolButtonDef[] = [
  { name: 'LINE', label: 'Line', title: 'LINE (L) — chained segments', icon: 'line' },
  { name: 'PLINE', label: 'Polyline', title: 'PLINE (PL) — connected polyline', icon: 'polyline' },
  { name: 'RECT', label: 'Rect', title: 'REC — rectangle by two corners', icon: 'rect' },
  { name: 'SPLINE', label: 'Spline', title: 'SPLINE (SPL) — NURBS control points, Enter to finish', icon: 'spline' },
  { name: 'CIRCLE', label: 'Circle', title: 'CIRCLE (C) — centre, then radius', icon: 'circle' },
  { name: 'ARC', label: 'Arc', title: 'ARC (A) — three point arc', icon: 'arc' },
];

/** Annotation, modification and editing tools. */
const TOOLS: ToolButtonDef[] = [
  { name: 'TEXT', label: 'Text', title: 'TEXT (T) — single line text', icon: 'text' },
  { name: 'HATCH', label: 'Hatch', title: 'HATCH (H) — pick a point inside a closed boundary', icon: 'hatch' },
  { name: 'DIM', label: 'Dimension', title: 'DIM (DLI) — linear dimension', icon: 'dim' },
  { name: 'DIMALIGNED', label: 'Aligned dim', title: 'DAL — aligned dimension', icon: 'daligned' },
  { name: 'ERASE', label: 'Erase', title: 'ERASE (E) — delete the selection or pick', icon: 'erase' },
  { name: 'MOVE', label: 'Move', title: 'MOVE (M) — move selected entities', icon: 'move' },
  { name: 'COPY', label: 'Copy', title: 'COPY (CO) — duplicate selected entities', icon: 'copy' },
  { name: 'ROTATE', label: 'Rotate', title: 'ROTATE (RO) — rotate about a base point', icon: 'rotate' },
  { name: 'SCALE', label: 'Scale', title: 'SCALE (SC) — scale about a base point', icon: 'scale' },
  { name: 'MIRROR', label: 'Mirror', title: 'MIRROR (MI) — mirror across a line', icon: 'mirror' },
  { name: 'OFFSET', label: 'Offset', title: 'OFFSET (O) — parallel copy at a distance', icon: 'offset' },
  { name: 'TRIM', label: 'Trim', title: 'TRIM (TR) — trim to visible edges', icon: 'trim' },
  { name: 'EXTEND', label: 'Extend', title: 'EXTEND (EX) — extend to visible edges', icon: 'extend' },
  { name: 'FILLET', label: 'Fillet', title: 'FILLET (F) — round the corner between two lines', icon: 'fillet' },
  { name: 'CHAMFER', label: 'Chamfer', title: 'CHAMFER (CHA) — bevel the corner between two lines', icon: 'chamfer' },
];

const ACTIONS: Array<{ label: string; title: string; icon: IconName; run: (editor: CadEditor) => void }> = [
  { label: 'Undo', title: 'Undo (Ctrl+Z)', icon: 'undo', run: (editor) => editor.undo() },
  { label: 'Redo', title: 'Redo (Ctrl+Y)', icon: 'redo', run: (editor) => editor.redo() },
  { label: 'Zoom extents', title: 'Zoom to drawing extents (Z)', icon: 'zoomExt', run: (editor) => editor.zoomExtents() },
];

const SNAP_SHORT: Record<SnapType, string> = {
  endpoint: 'END',
  intersection: 'INT',
  midpoint: 'MID',
  center: 'CEN',
  quadrant: 'QUA',
  perpendicular: 'PER',
  tangent: 'TAN',
  grid: 'GRID',
};

const SNAP_LABELS: Record<SnapType, string> = {
  endpoint: 'Endpoint',
  intersection: 'Intersection',
  midpoint: 'Midpoint',
  center: 'Center',
  quadrant: 'Quadrant',
  perpendicular: 'Perpendicular',
  tangent: 'Tangent',
  grid: 'Grid',
};

function requireElement(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing element #${id}`);
  return element;
}

function formatLength(value: number, decimals: number): string {
  return value.toFixed(decimals);
}

export function createUi(
  editor: CadEditor,
  renderer: CadRenderer,
  graph?: GraphStore | null,
): FileActions {
  const primitiveHost = requireElement('primitive-buttons');
  const toolHost = requireElement('tool-buttons');
  const constraintHost = requireElement('constraint-buttons');
  const workflowHost = requireElement('workflow-buttons');
  const fileHost = requireElement('file-buttons');
  const snapHost = requireElement('snap-buttons');
  const promptEl = requireElement('prompt');
  const commandInput = requireElement('command-line') as HTMLInputElement;
  const coordsEl = requireElement('status-coords');
  const snapEl = requireElement('status-snap');
  const messageEl = requireElement('status-message');
  const selectionEl = requireElement('status-selection');
  const constraintsEl = requireElement('status-constraints');
  const entitiesEl = requireElement('status-entities');
  const zoomEl = requireElement('status-zoom');
  const backendEl = requireElement('status-backend');
  const dynEl = requireElement('dyn-input');
  const orthoEl = requireElement('status-ortho');

  backendEl.textContent = renderer.backend.toUpperCase();
  backendEl.title = 'Rendering backend';

  let cursorScreen = { x: 0, y: 0 };
  const dynRows: Array<{ root: HTMLDivElement; label: HTMLSpanElement; value: HTMLSpanElement }> = [];

  const ensureDynRows = (count: number): void => {
    while (dynRows.length < count) {
      const root = document.createElement('div');
      root.className = 'dyn-row';
      const label = document.createElement('span');
      label.className = 'dyn-label';
      const value = document.createElement('span');
      value.className = 'dyn-value';
      root.append(label, value);
      dynEl.appendChild(root);
      dynRows.push({ root, label, value });
    }
    while (dynRows.length > count) dynRows.pop()?.root.remove();
  };

  const renderDyn = (): void => {
    const fields = editor.fields;
    if (fields.length === 0) {
      dynEl.hidden = true;
      return;
    }
    dynEl.hidden = false;
    ensureDynRows(fields.length);

    const typed = commandInput.value.trim();
    const typedIsNumber = /^[-+]?(\d+\.?\d*|\.\d+)$/.test(typed);

    fields.forEach((field, index) => {
      const row = dynRows[index]!;
      const active = index === editor.activeFieldIndex;
      row.root.classList.toggle('active', active);
      row.label.textContent = field.label;
      const suffix = field.unit === 'deg' ? '°' : '';
      const showTyped = active && typedIsNumber;
      row.value.textContent = `${showTyped ? typed : field.value.toFixed(field.decimals)}${suffix}`;
      row.value.classList.toggle('typing', showTyped);
    });

    const stage = dynEl.parentElement;
    if (stage) {
      const stageRect = stage.getBoundingClientRect();
      const width = dynEl.offsetWidth;
      const height = dynEl.offsetHeight;
      let x = cursorScreen.x + 18;
      let y = cursorScreen.y + 18;
      if (x + width > stageRect.width - 6) x = Math.max(6, cursorScreen.x - width - 12);
      if (y + height > stageRect.height - 6) y = Math.max(6, cursorScreen.y - height - 12);
      dynEl.style.left = `${x}px`;
      dynEl.style.top = `${y}px`;
    }
  };

  const toolButtons = new Map<string, HTMLButtonElement>();

  const createToolButton = (tool: ToolButtonDef, host: HTMLElement): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tool icon';
    button.title = tool.title;
    button.setAttribute('aria-label', tool.label);
    button.innerHTML = ICONS[tool.icon];
    button.addEventListener('click', () => {
      editor.startTool(tool.name);
      refresh();
      commandInput.focus();
    });
    host.appendChild(button);
    toolButtons.set(tool.name, button);
    return button;
  };

  for (const tool of PRIMITIVES) createToolButton(tool, primitiveHost);
  for (const tool of TOOLS) createToolButton(tool, toolHost);

  for (const action of ACTIONS) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tool ghost icon';
    button.title = action.title;
    button.setAttribute('aria-label', action.label);
    button.innerHTML = ICONS[action.icon];
    button.addEventListener('click', () => {
      action.run(editor);
      refresh();
      commandInput.focus();
    });
    workflowHost.appendChild(button);
  }

  const fileActions = installFileActions(editor, fileHost, graph);

  // Constraint kinds are switches inside one dropdown: clicking an item toggles it and the menu
  // stays open, so a whole set can be parked or re-enabled in one visit.
  createMenuButton(constraintHost, {
    icon: 'constraint',
    label: 'Constrain',
    title: 'Constraint switches — click to enable or park a kind (menu stays open)',
    items: () => {
      const enforced = editor.settings.enabledConstraints;
      const switches: MenuEntry[] = CONSTRAINT_KINDS.map((kind) => ({
        label: CONSTRAINT_LABELS[kind],
        hint: CONSTRAINT_SHORT[kind],
        checked: enforced.has(kind),
        keepOpen: true,
        run: () => {
          if (enforced.has(kind)) enforced.delete(kind);
          else enforced.add(kind);
          editor.message(
            `CONSTRAINT  ${CONSTRAINT_LABELS[kind]} ${enforced.has(kind) ? 'on' : 'off'}`,
          );
          refresh();
        },
      }));

      return [
        ...switches,
        { label: 'Solve now', hint: 'SOLVE', run: () => editor.execute('SOLVE') },
        {
          label: 'Delete all constraints',
          hint: 'DC',
          run: () => editor.execute('DELCONSTRAINTS'),
        },
      ];
    },
  });

  const snapButtons = new Map<SnapType, HTMLButtonElement>();
  for (const type of SNAP_ORDER) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tool snap';
    button.textContent = SNAP_SHORT[type];
    button.title = `${SNAP_LABELS[type]} snap`;
    button.addEventListener('click', () => {
      const enabled = editor.snapEngine.settings.enabled;
      if (enabled.has(type)) enabled.delete(type);
      else enabled.add(type);
      refresh();
      commandInput.focus();
    });
    snapHost.appendChild(button);
    snapButtons.set(type, button);
  }

  const gridButton = document.createElement('button');
  gridButton.type = 'button';
  gridButton.className = 'tool ghost';
  gridButton.textContent = 'Grid';
  gridButton.title = 'Toggle grid (F8)';
  gridButton.addEventListener('click', () => {
    editor.setGridVisible(!editor.settings.grid.visible);
    refresh();
    commandInput.focus();
  });
  snapHost.appendChild(gridButton);

  const refresh = (): void => {
    const activeName = editor.activeTool?.name ?? '';
    for (const [name, button] of toolButtons) {
      button.classList.toggle('active', name === activeName);
    }
    const enabled = editor.snapEngine.settings.enabled;
    for (const [type, button] of snapButtons) {
      button.classList.toggle('on', enabled.has(type));
    }
    gridButton.classList.toggle('active', editor.settings.grid.visible);
    orthoEl.textContent = editor.angleConstraint ? `CTRL ${editor.settings.polarStepDeg}°` : '';
    promptEl.textContent = editor.promptState.text;
    selectionEl.textContent = `${editor.selection.size} selected`;
    constraintsEl.textContent = `${editor.doc.constraintCount} constraints`;
    entitiesEl.textContent = `${editor.doc.entityCount} entities`;
    zoomEl.textContent = `${Math.round(editor.viewport.scale * 100)}%`;
  };

  // ------------------------------------------------------------- command line

  const commandHistory: string[] = [];
  let historyIndex = -1;

  commandInput.addEventListener('keydown', (event) => {
    const empty = commandInput.value.length === 0;

    if (event.key === 'Enter') {
      const value = commandInput.value;
      commandInput.value = '';
      if (value.trim().length > 0) {
        commandHistory.push(value);
        historyIndex = commandHistory.length;
      }
      editor.execute(value);
      refresh();
      renderDyn();
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowUp') {
      if (commandHistory.length === 0) return;
      historyIndex = Math.max(0, Math.min(historyIndex - 1, commandHistory.length - 1));
      commandInput.value = commandHistory[historyIndex] ?? '';
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowDown') {
      if (commandHistory.length === 0) return;
      historyIndex = Math.min(historyIndex + 1, commandHistory.length);
      commandInput.value = historyIndex >= commandHistory.length ? '' : commandHistory[historyIndex] ?? '';
      event.preventDefault();
      return;
    }

    if (event.key === 'Escape') {
      commandInput.value = '';
      // One press always ends what is being drawn: a shape never needs two Escapes. With no
      // command running this just clears the selection.
      editor.keyDown({ key: 'Escape', ctrlKey: false, shiftKey: false });
      refresh();
      renderDyn();
      event.preventDefault();
      return;
    }

    if (event.key === 'Tab' && empty) {
      if (editor.fields.length > 0) editor.cycleField();
      else editor.cycleSnap();
      refresh();
      event.preventDefault();
      return;
    }

    if (empty && (event.key === 'Delete' || event.key === 'Backspace')) {
      editor.deleteSelection();
      refresh();
      event.preventDefault();
      return;
    }

    if (empty && event.ctrlKey && event.key.toLowerCase() === 'z') {
      if (event.shiftKey) editor.redo();
      else editor.undo();
      refresh();
      event.preventDefault();
    }
  });

  // ------------------------------------------------------------------- events

  editor.events.on('cursor', (state) => {
    cursorScreen = state.screen;
    const decimals = editor.settings.lengthDecimals;
    coordsEl.textContent = `${formatLength(state.world.x, decimals)}, ${formatLength(state.world.y, decimals)}`;
    snapEl.textContent = state.snap
      ? `SNAP ${SNAP_LABELS[state.snap.type].toUpperCase()}`
      : 'SNAP —';
    renderDyn();
  });

  editor.events.on('fields', () => renderDyn());
  commandInput.addEventListener('input', () => renderDyn());

  editor.events.on('prompt', () => refresh());
  editor.events.on('selection', () => refresh());
  editor.events.on('document', () => refresh());
  editor.events.on('view', () => refresh());
  editor.events.on('overlay', () => refresh());
  let messageTimer = 0;
  editor.events.on('message', (text) => {
    messageEl.textContent = text;
    if (messageTimer) window.clearTimeout(messageTimer);
    messageTimer = window.setTimeout(() => {
      messageEl.textContent = '';
    }, 6000);
  });

  window.addEventListener('keydown', (event) => {
    if (event.key === 'F3') {
      const enabled = editor.snapEngine.settings.enabled;
      const anyEnabled = enabled.size > 0;
      enabled.clear();
      if (!anyEnabled) {
        for (const type of SNAP_ORDER) enabled.add(type);
      }
      refresh();
      event.preventDefault();
    }
  });

  refresh();
  renderDyn();
  attachToolbarScrolling();
  commandInput.focus();
  return fileActions;
}

/**
 * Narrow windows keep the toolbar at two rows: the rows scroll sideways on the wheel instead of
 * wrapping into more rows, and no scrollbar is drawn.
 */
function attachToolbarScrolling(): void {
  for (const row of document.querySelectorAll<HTMLElement>('.toolbar-row')) {
    if (row.dataset['scrolling'] === '1') continue;
    row.dataset['scrolling'] = '1';
    row.addEventListener(
      'wheel',
      (event) => {
        if (row.scrollWidth <= row.clientWidth + 1) return;
        const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
        if (delta === 0) return;
        event.preventDefault();
        row.scrollLeft += delta;
      },
      { passive: false },
    );
  }
}
